/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useEffect, useState } from "react";
import { Alert, Box, useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import Backdrop from "@mui/material/Backdrop";
import Fade from "@mui/material/Fade";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { StateMapping, STATES_MAPPING } from "../../utils/Constants";
import Grid from "@mui/material/Grid";

import { Company, Effect, ModalBase, Nullable, ServiceLocation } from "../../utils/Interfaces";
import { selectExistingCompanies } from "../office-companies/companiesSlice";
import { createSite, createSiteValidation, updateSites } from "./workSitesFunctions";
import { selectExistingSites } from "./workSitesSlice";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

export interface CreateSiteModalProps extends ModalBase {
    onSiteCreated?: Effect;
    defaultCompany?: Company;
}

const CreateSiteModal = (props: CreateSiteModalProps) => {
    const { open, handleClose, onSiteCreated, defaultCompany } = props;

    const dispatch = useAppDispatch();

    const existingCompanies = useAppSelector(selectExistingCompanies);
    const [validationErrorMessage, setValidationErrorMessage] = useState<string>("");

    const [storeNo, setStoreNo] = useState<string>("");
    const [streetAddress, setStreetAddress] = useState<string>("");
    const [storeType, setStoreType] = useState<string>("");
    const [city, setCity] = useState<string>("");
    const [state, setState] = useState<StateMapping>(STATES_MAPPING[46]);
    const [company, setCompany] = useState<Company>(defaultCompany ? defaultCompany : existingCompanies[0]);
    const [zipCode, setZipCode] = useState<Nullable<string>>("");
    const [comments, setComments] = useState<Nullable<string>>(null);

    const existingSites: ServiceLocation[] = useAppSelector(selectExistingSites);

    const theme = useTheme();

    function siteToCreate(key: keyof ServiceLocation, value: string | number | Company | StateMapping): ServiceLocation {
        let siteTemplate: ServiceLocation = {
            // ID is part of the standard site model, but cannot be used here because the site has not been created yet.
            id: NaN,
            store_no: storeNo,
            street_address: streetAddress,
            store_type: storeType,
            city: city,
            state: state.abbrev,
            company: company,
            zip_code: zipCode ? parseInt(zipCode) : null,
            comments: comments,
        };

        // @ts-ignore
        siteTemplate[key] = value;

        return siteTemplate;
    }

    useEffect(() => {
        setValidationErrorMessage(createSiteValidation(siteToCreate("id", NaN), existingSites));
    }, [existingCompanies, existingSites, dispatch]);

    return (
        <Box>
            <Modal
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
                open={open}
                onClose={handleClose}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={open}>
                    <Box
                        sx={{
                            backgroundColor: theme.palette.background.paper,
                            border: "1px solid #000",
                            boxShadow: theme.shadows[5],
                            padding: theme.spacing(2, 4, 3),
                            zIndex: 1000,
                        }}
                    >
                        <Alert
                            severity={validationErrorMessage === "" ? "success" : "error"}
                            sx={{
                                mb: 2,
                            }}
                        >
                            {validationErrorMessage === "" ? "All changes are valid." : validationErrorMessage}
                        </Alert>

                        <Box component="form">
                            <TextField
                                onChange={(event) => {
                                    setStoreNo(event.target.value);
                                    setValidationErrorMessage(
                                        createSiteValidation(siteToCreate("store_no", event.target.value), existingSites)
                                    );
                                }}
                                margin="normal"
                                required
                                fullWidth
                                id="store_no"
                                label="Store Number"
                                name="store_no"
                                value={storeNo}
                            />
                            <TextField
                                onChange={(event) => {
                                    setStreetAddress(event.target.value);
                                    setValidationErrorMessage(
                                        createSiteValidation(siteToCreate("street_address", event.target.value), existingSites)
                                    );
                                }}
                                margin="normal"
                                required
                                fullWidth
                                id="street_address"
                                label="Street Address"
                                name="street_address"
                                value={streetAddress}
                            />
                            <TextField
                                onChange={(event) => {
                                    setCity(event.target.value);
                                    setValidationErrorMessage(
                                        createSiteValidation(siteToCreate("city", event.target.value), existingSites)
                                    );
                                }}
                                margin="normal"
                                required
                                fullWidth
                                id="city"
                                label="City"
                                name="city"
                                value={city}
                            />

                            <Grid container spacing={3}>
                                <Grid item xs={6}>
                                    <Autocomplete
                                        disablePortal
                                        onChange={(_event, value) => {
                                            setState(value ? value : STATES_MAPPING[46]);
                                            setValidationErrorMessage(
                                                createSiteValidation(
                                                    siteToCreate("state", value ? value : STATES_MAPPING[46]),
                                                    existingSites
                                                )
                                            );
                                        }}
                                        sx={{ mt: 2 }}
                                        id="state"
                                        getOptionKey={(option: StateMapping) => option.abbrev}
                                        options={STATES_MAPPING}
                                        value={state}
                                        getOptionLabel={(option: StateMapping) => option.name}
                                        renderInput={(params) => <TextField required {...params} label="State" />}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        onChange={(event) => {
                                            setZipCode(event.target.value);
                                            setValidationErrorMessage(
                                                createSiteValidation(siteToCreate("zip_code", event.target.value), existingSites)
                                            );
                                        }}
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="zip_code"
                                        label="Zip Code"
                                        name="zip_code"
                                        value={zipCode}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container spacing={3}>
                                <Grid item xs={6}>
                                    <Autocomplete
                                        disabled={defaultCompany !== null && defaultCompany !== undefined}
                                        disablePortal
                                        onChange={(_event, value) => {
                                            setCompany(value ? value : existingCompanies[0]);
                                            setValidationErrorMessage(
                                                createSiteValidation(
                                                    siteToCreate("company", value ? value : existingCompanies[0]),
                                                    existingSites
                                                )
                                            );
                                        }}
                                        sx={{ mt: 2 }}
                                        id="company"
                                        getOptionKey={(option: Company) => option.id}
                                        options={existingCompanies}
                                        value={company}
                                        getOptionLabel={(option: Company) => option.name}
                                        renderInput={(params) => <TextField required {...params} label="Company" />}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        onChange={(event) => {
                                            setStoreType(event.target.value);
                                            setValidationErrorMessage(
                                                createSiteValidation(
                                                    siteToCreate("store_type", event.target.value),
                                                    existingSites
                                                )
                                            );
                                        }}
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="store_type"
                                        label="Store Type"
                                        name="store_type"
                                        value={storeType}
                                    />
                                </Grid>
                            </Grid>

                            <TextField
                                onChange={(event) => {
                                    setComments(event.target.value);
                                    setValidationErrorMessage(
                                        createSiteValidation(siteToCreate("comments", event.target.value), existingSites)
                                    );
                                }}
                                margin="normal"
                                fullWidth
                                multiline
                                rows={4}
                                id="comments"
                                label="Site Comments"
                                name="comments"
                                value={comments === null || comments === undefined ? "" : comments}
                            />

                            <Button
                                onClick={() => {
                                    createSite(siteToCreate("id", NaN), dispatch, () => {
                                        updateSites(dispatch, () => {});
                                        if (onSiteCreated) {
                                            onSiteCreated();
                                        }
                                    });

                                    handleClose();
                                }}
                                fullWidth
                                variant="contained"
                                sx={{ mt: 3, mb: 2 }}
                                color="primary"
                                disabled={validationErrorMessage !== ""}
                            >
                                Submit
                            </Button>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </Box>
    );
};

export default CreateSiteModal;
